import type { ReactElement } from 'react';

import { Icon } from '@procurify/ui'
import { type IntlShape } from 'react-intl'

import {
  BillItemExceptionReceiveCostContent,
  BillItemExceptionReceiveQuantityContent,
  BillItemSummaryReceivedCostContent,
  BillItemSummaryReceivedQuantityContent,
} from '@webapp/components/BillItemPopover'
import { type BillItem } from '@webapp/domain/accountsPayable/entities'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

interface UseAPItemReceivedCellReturn {
  iconContent: ReactElement
  popoverContent: ReactElement
  hasException: boolean
}

const hasBillItemException = (billItem: BillItem) =>
  !!(
    billItem.receiveCostException ||
    billItem.receiveQuantityException ||
    billItem.zeroReceivedQuantityException
  )

const getBillItemReceivedCostContent = (billItem: BillItem) => {
  if (billItem.receiveCostException) {
    return (
      <BillItemExceptionReceiveCostContent
        exception={billItem.receiveCostException}
      />
    )
  }

  return (
    <BillItemSummaryReceivedCostContent
      currencyName={billItem.currencyName}
      itemSummary={billItem.itemSummary}
    />
  )
}

const getBillItemReceivedQuantityContent = (billItem: BillItem) => {
  if (billItem.receiveCostException) {
    return (
      <BillItemExceptionReceiveCostContent
        exception={billItem.receiveCostException}
        receivedQuantity={billItem.itemSummary.receivedQuantity}
        purchasedQuantity={billItem.itemSummary.purchasedQuantity}
      />
    )
  }

  if (billItem.receiveQuantityException) {
    return (
      <BillItemExceptionReceiveQuantityContent
        exception={billItem.receiveQuantityException}
      />
    )
  }

  if (billItem.zeroReceivedQuantityException) {
    return (
      <BillItemExceptionReceiveQuantityContent
        exception={billItem.zeroReceivedQuantityException}
      />
    )
  }

  if (billItem.billByCost) {
    return (
      <BillItemSummaryReceivedCostContent
        currencyName={billItem.currencyName}
        itemSummary={billItem.itemSummary}
      />
    )
  }

  return (
    <BillItemSummaryReceivedQuantityContent
      itemSummary={billItem.itemSummary}
    />
  )
}

const getBillItemPopoverContent = (billItem: BillItem) => {
  if (billItem.isReceivedByCost) {
    return getBillItemReceivedCostContent(billItem)
  }

  if (billItem.isReceivedByQuantity) {
    return getBillItemReceivedQuantityContent(billItem)
  }

  return null
}

const getBillItemIconContent = (
  billItem: BillItem,
  intl: IntlShape
): ReactElement => {
  if (hasBillItemException(billItem)) {
    return (
      <Icon
        icon='close'
        size={16}
        color='danger'
        ariaLabel={intl.formatMessage(messages.exceptionIconLabel)}
      />
    )
  }

  return (
    <Icon
      icon='question'
      size={16}
      ariaLabel={intl.formatMessage(messages.informationIconLabel)}
    />
  )
}

export const useAPItemReceivedCell = (
  billItem: BillItem
): UseAPItemReceivedCellReturn => {
  const intl = useProcurifyIntl()

  return {
    iconContent: getBillItemIconContent(billItem, intl),
    popoverContent: getBillItemPopoverContent(billItem),
    hasException: hasBillItemException(billItem),
  }
}
